import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';
import { Spinner } from 'react-bootstrap';
import '../styles/RecentArticles.css';
import { useNavigate } from 'react-router-dom';
import useDynamicPath from '../hooks/useDynamicPath';
import constants from '../constants.json' assert { type: 'json' };
import { BiCalendar, BiUser } from 'react-icons/bi';
import { DateTime } from 'luxon';

const BASE_WIDTH = 320;
const TRANSITION_DURATION = 300;
const SLIDE_LEFT = -1;
const CAROUSEL_SECTIONS = 3;
const FLEX_CENTER_CLASS = 'd-flex justify-content-center align-items-center';

export default function RecentArticles() {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [translateX, setTranslateX] = useState(0);
    const [animate, setAnimate] = useState(true);
    const moveCount = useRef(0);
    const disabled = useRef(false);
    const [originalArticles, setOriginalArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const articleBasePath = articlePath[0].pathBeforeParams;
    const fetchRecentArticles = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);
            const params = new URLSearchParams();
            params.append('page', 0);
            params.append('limit', 10);
            params.append('sortBy', 'CREATED');
            params.append('order', 'DESC');
            const response = await fetch(new URL('/api/articles', process.env.APP_URL));
            const data = await response.json();
            if (data.articles) {
                setOriginalArticles(data.articles);
            }
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchRecentArticles();
    }, [fetchRecentArticles]);

    const fullList = useMemo(() => [...originalArticles, ...originalArticles, ...originalArticles], [originalArticles]);
    const slideWidth = BASE_WIDTH;

    useEffect(() => {
        setArticles(fullList);
        setTranslateX(fullList.length / CAROUSEL_SECTIONS * slideWidth * SLIDE_LEFT);
        moveCount.current = fullList.length / CAROUSEL_SECTIONS;
    }, [fullList]);

    const reset = () => {
        disabled.current = true;
        setTimeout(() => {
            const resetIndex = fullList.length / CAROUSEL_SECTIONS;
            setTranslateX(resetIndex * slideWidth * SLIDE_LEFT);
            setAnimate(false);
            moveCount.current = resetIndex;
        }, TRANSITION_DURATION);
    };

    const next = () => {
        if (disabled.current) {
            return;
        }
        setTranslateX((prev) => prev - slideWidth);
        moveCount.current++;
        if (moveCount.current === fullList.length - fullList.length / CAROUSEL_SECTIONS) {
            reset();
        }
    };

    const prev = () => {
        if (disabled.current) {
            return;
        }
        setTranslateX((prev) => prev + slideWidth);
        moveCount.current--;
        if (moveCount.current === 0) {
            reset();
        }
    };

    useEffect(() => {
        if (!animate) {
            setTimeout(() => setAnimate(true), TRANSITION_DURATION);
        } else {
            disabled.current = false;
        }
    }, [animate]);

    return (
        <div
            className={'carousel-container'}>
            <h3
                className={'title'}>OUR BLOG</h3>
            <p
                className={'subtitle'}>A few of our recent posts</p>
            {articles?.length ? <div>
                <div
                    className={'carousel-wrapper'}>
                    <button
                        onClick={prev}
                        className={'arrow-btn'}><MdArrowBackIos /></button>
                    <div
                        className={'carousel-viewport'}>
                        <div
                            className={'carousel-slider'}
                            style={{
                                transform: `translateX(${translateX}px)`,
                                transition: animate ? `transform ${TRANSITION_DURATION}ms ease-in-out` : 'none',
                                width: `${articles.length * slideWidth}px`,
                                height: '290px',
                            }}
                        >
                            {articles.map((article, idx) => (
                                <div
                                    className={'card-container'}
                                    key={`${article.id}-${idx}`}
                                    style={{ width: `${slideWidth}px` }}>
                                    <img
                                        src={article.imageUrl}
                                        alt={article.title}
                                        className={'card-img'} />
                                    <div
                                        className={'card-content'}>
                                        <h5
                                            className={'card-title'}>{article.title}</h5>
                                        <div
                                            className={'card-meta'}>
                                            {article.author && <span
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                }}><BiUser
                                                    size={16} /> {article.author}</span>}
                                            <span
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                }}><BiCalendar
                                                    size={16} /> {DateTime.fromISO(article.createdAt).toFormat('yyyy LLL dd')}</span>
                                        </div>
                                        <p
                                            className={'card-summary'}>{article.seoDescription}</p>
                                        <button
                                            onClick={() => {
                                                navigate(`${articleBasePath}/${article.handle}`);
                                            }}
                                            className={'read-more'}>Read More</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={next}
                        className={'arrow-btn'}><MdArrowForwardIos /></button>
                </div>
                <button
                    onClick={() => navigate('/blogs')}
                    className={'see-more'}>SEE MORE POSTS →</button>
            </div> : null}
            {loading ? <div
                className={FLEX_CENTER_CLASS}>
                <Spinner />
            </div> : null}
            {(!loading && !articles?.length && !error) ? <div
                className={FLEX_CENTER_CLASS}>
                <p>No articles found</p>
            </div> : null}
            {error && !loading ? <div
                className={FLEX_CENTER_CLASS}>
                <p>Failed to load articles. Please try again later.</p>
            </div> : null}
        </div>
    );
}
